
import useGroupConvertor, { type ApiGroup, type Group } from './group';

import type { ApiEntity } from '@/common/domain/api';
import useFleetConvertor, { type ApiFleetAttributes, type Fleet } from '@/fleet/domain/fleet';


export enum PacketType {
    RESERVED = 'reserved',
    OVERFLOW = 'overflow',

    SHARED = 'shared',

    // Add more types as needed
}

// Packet config sub-components (parking-api). Packet stores the resolved
// (per user-group) values; no *Overridable pairs.
export interface PacketConfigFeatures {
    enablePersonalFleet?: boolean;
    enableGuest?: boolean;
    enableReservation?: boolean;
    enableAccessProfile?: boolean;
    enableGroupFleet?: boolean;
    enableDistantRetrieval?: boolean;
    // When true, the group's admins are also its fleet's admins (no separate fleet-admin group).
    useUserGroupAdminAsFleetAdmin?: boolean;
}

export interface PacketConfigPayment {
    enablePaymentViaParkstory?: boolean;
    skipPayment?: boolean; // was the user-group isVIP flag
    enableCharging?: boolean;
}

export interface PacketConfig {
    features?: PacketConfigFeatures;
    payment?: PacketConfigPayment;
}

// Spec defaults for a freshly created packet config.
export const defaultPacketConfig: PacketConfig = {
    features: {
        enablePersonalFleet: true,
        enableGuest: true,
        enableReservation: true,
        enableAccessProfile: true,
        enableGroupFleet: true,
        enableDistantRetrieval: false,
        useUserGroupAdminAsFleetAdmin: false,
    },
    payment: {
        enablePaymentViaParkstory: true,
        skipPayment: false,
        enableCharging: false,
    },
};

export interface ApiPacket {
    id: string;
    documentId?: string;
    resaClassRef: string;
    reservedNumber: number;
    overflowNumber?: number;
    isOverflowFixed: boolean;
    config?: PacketConfig;
    userGroup?: { data: ApiGroup } | string;
    fleet?: { data: ApiEntity<ApiFleetAttributes> } | string;
}

export interface Packet {
    id?: string;
    documentId?: string;
    resaClassRef: string;
    reservedNumber: number;
    overflowNumber?: number;
    isOverflowFixed: boolean;
    config?: PacketConfig;
    userGroup?: Group;
    fleet?: Fleet;
}

const fixedSlots = (packet: Packet): number | undefined => {
    if (packet === undefined) {
        return undefined;
    }
    return packet?.reservedNumber;
};

const floatingSlots = (packet: Packet): number | undefined => {
    if (packet === undefined) {
        return undefined;
    }
    return packet?.overflowNumber;
};

const hasData = <T>(value: { data: T } | string | undefined): value is { data: T } => {
    return typeof value === 'object' && value !== null && 'data' in value;
};

const packetToApiPacket = (packet: Packet): ApiPacket => {
    return {
        isOverflowFixed: packet.isOverflowFixed,
        overflowNumber: packet.overflowNumber,
        resaClassRef: packet.resaClassRef,
        reservedNumber: packet.reservedNumber ?? 0,
        config: packet.config,
        id: packet.id ?? '',
    };
};

const apiPacketToPacket = (apiResponse: ApiPacket): Packet => {
    const { resaClassRef, overflowNumber, reservedNumber, isOverflowFixed, config, userGroup, fleet } = apiResponse;

    return ({
        config,
        documentId: apiResponse.documentId,
        fleet: hasData(fleet) ? useFleetConvertor.apiFleetToFleet(fleet.data) : undefined,
        id: apiResponse.id,
        isOverflowFixed,
        overflowNumber,
        resaClassRef,
        reservedNumber,
        userGroup: hasData(userGroup) ? (useGroupConvertor.apiGroupToGroup(userGroup.data) ?? undefined) : undefined
    });
};

const apiPacketsToPackets = (apiPackets: ApiPacket[]): Packet[] => {

    return apiPackets.map((apiPacket: ApiPacket) => {
        return apiPacketToPacket(apiPacket);
    });
};

const usePacket = {
    apiPacketToPacket,
    apiPacketsToPackets,
    fixedSlots,
    floatingSlots,
    packetToApiPacket
};

export default usePacket;